import React, { useEffect, useRef } from 'react';
import './Spinner.css';

interface SpinnerProps {
    className?: string;
    mini?: boolean;
}

const AvmParticleCanvas: React.FC<{ mini: boolean }> = ({ mini }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        let frameId = 0;
        let width = 0;
        let height = 0;
        let particles: Array<{
            tx: number;
            ty: number;
            x: number;
            y: number;
            z: number;
            speed: number;
            phase: number;
        }> = [];

        const sampleText = (nextWidth: number, nextHeight: number) => {
            const offscreen = document.createElement('canvas');
            const off = offscreen.getContext('2d');
            if (!off) return [];

            offscreen.width = nextWidth;
            offscreen.height = nextHeight;
            off.fillStyle = '#fff';
            off.font = `800 ${Math.max(mini ? 12 : 46, Math.floor(nextWidth * 0.3))}px Inter, system-ui, sans-serif`;
            off.textAlign = 'center';
            off.textBaseline = 'middle';
            off.fillText('AVM', nextWidth / 2, nextHeight / 2 + (mini ? 0 : 2));

            const data = off.getImageData(0, 0, nextWidth, nextHeight).data;
            const points: Array<{ x: number; y: number }> = [];
            const step = Math.max(mini ? 3 : 5, Math.floor(nextWidth / (mini ? 22 : 58)));

            for (let y = 0; y < nextHeight; y += step) {
                for (let x = 0; x < nextWidth; x += step) {
                    if (data[(y * nextWidth + x) * 4 + 3] > 120) {
                        points.push({ x, y });
                    }
                }
            }

            return points;
        };

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            const ratio = Math.min(window.devicePixelRatio || 1, 2);
            const nextWidth = Math.max(1, Math.floor(rect.width * ratio));
            const nextHeight = Math.max(1, Math.floor(rect.height * ratio));

            if (nextWidth === width && nextHeight === height) return;

            width = nextWidth;
            height = nextHeight;
            canvas.width = width;
            canvas.height = height;

            particles = sampleText(width, height).map((point, index) => ({
                tx: point.x,
                ty: point.y,
                x: Math.random() * width,
                y: Math.random() * height,
                z: Math.random() * 28,
                speed: 0.045 + Math.random() * 0.035,
                phase: index * 0.13,
            }));
        };

        const draw = (time: number) => {
            resize();
            ctx.clearRect(0, 0, width, height);

            const cycle = (Math.sin(time * 0.0017) + 1) / 2;
            const gather = 0.35 + cycle * 0.65;
            const driftScaleX = mini ? 12 : 72;
            const driftScaleY = mini ? 8 : 42;

            particles.forEach((particle) => {
                const driftX = Math.cos(time * 0.0014 + particle.phase) * driftScaleX * (1 - gather);
                const driftY = Math.sin(time * 0.0011 + particle.phase) * driftScaleY * (1 - gather);
                particle.x += (particle.tx + driftX - particle.x) * particle.speed;
                particle.y += (particle.ty + driftY - particle.y) * particle.speed;

                const size = (mini ? 0.75 : 1.2) + particle.z * (mini ? 0.012 : 0.025);
                const alpha = 0.38 + gather * 0.62;
                ctx.beginPath();
                ctx.fillStyle = `rgba(88, 230, 255, ${alpha})`;
                ctx.shadowColor = 'rgba(88, 230, 255, 0.55)';
                ctx.shadowBlur = (mini ? 3 : 8) + particle.z * 0.3;
                ctx.arc(particle.x, particle.y, size, 0, Math.PI * 2);
                ctx.fill();
            });

            frameId = window.requestAnimationFrame(draw);
        };

        frameId = window.requestAnimationFrame(draw);

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [mini]);

    return (
        <div className={`avm-loader ${mini ? 'avm-loader--mini' : ''}`} aria-hidden="true">
            <canvas ref={canvasRef} className="avm-loader__canvas" />
        </div>
    );
};

const Spinner: React.FC<SpinnerProps> = ({ className = "", mini = false }) => (
    <div className={`all-loader-container ${mini ? 'mini' : ''} ${className}`} role={mini ? undefined : 'status'} aria-label={mini ? undefined : 'Загрузка'}>
        <AvmParticleCanvas mini={mini} />
    </div>
);

export default Spinner;
